import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { AlertTriangle, Send, RefreshCw } from 'lucide-react';
import CpscStatusChecker from './CpscStatusChecker';
import CpscTable, { CPSC_ROW_CLASS, CPSC_TH_CLASS } from './CpscTable';
import { STATUS as DRAFT_STATUS } from './CpscDrafts'; // ONE status vocabulary (§8.2)

/**
 * CpscSubmissions — CHA-267 PR-3 (Ruling 5). The IN-FLIGHT submissions worklist: drafts currently moving
 * through the CPSC pipeline (submitting / awaiting certification / certifying / pending / failed), plus
 * the per-style status checker tool below. Deliberate split vs its siblings (§8.2 — same collection,
 * distinct jobs): Audit & History = the immutable everything timeline; Drafts = the gated lifecycle
 * ENGINE (all actions live there); THIS view = "what is at CPSC right now and what needs a nudge" —
 * display-only, no actions, no writes. There is NO submissions collection: submission state lives ON the
 * draft docs (Stage-1-verified), so this is a status-filtered client read of cpsc_drafts.
 */
const s = (v) => (v == null ? '' : String(v));

// The in-flight slice of the draft state machine (cpscDraft.js STATES): everything between the operator
// pressing Submit and a terminal certified/superseded — including the failure states an operator must
// react to. Authoring states (draft/needs_confirmation/…) belong to Drafts; certified is Register's.
const IN_FLIGHT = ['submitted_validation', 'awaiting_certification', 'submission_error', 'certifying', 'certification_pending', 'certification_failed'];
const tone = (st) => (st === 'submission_error' || st === 'certification_failed' ? 'text-carbon-red-60 font-medium'
  : st === 'awaiting_certification' ? 'text-carbon-green-50' : 'text-carbon-orange-70');

export default function CpscSubmissions({ userProfile } = {}) {
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';

  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true); setError(null);
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, 'cpsc_drafts', tenantId, 'drafts'));
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((d) => IN_FLIGHT.includes(s(d.status))));
    } catch (err) {
      console.error('cpsc_drafts read failed', err);
      setError('Could not load in-flight submissions (check console).');
    } finally { setLoading(false); }
  }, [isAdmin, tenantId]);
  useEffect(() => { load(); }, [load]);

  if (!isAdmin) return <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">Submissions are restricted to administrators.</div>;

  return (
    <div className="font-plex text-carbon-ink space-y-4">
      <div>
        <h3 className="text-base font-semibold text-carbon-ink flex items-center gap-1.5"><Send size={16} /> CPSC — Submissions (in flight)</h3>
        <p className="text-sm text-carbon-gray-70">Drafts currently moving through the CPSC pipeline. All actions (retry, reopen, certify) live under <strong>Drafts</strong>; the immutable timeline lives under <strong>Audit &amp; History</strong>. Use the checker below to look up any production style.</p>
      </div>

      <div className="bg-carbon-white border border-carbon-border p-3" aria-label="in-flight submissions">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-plex-mono text-[10px] uppercase tracking-[0.12em] font-semibold text-carbon-gray-70">In flight ({rows.length})</h4>
          <button onClick={load} disabled={loading} className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-gray-30 text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-50" aria-label="refresh submissions">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
        {error && <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> {error}</div>}
        {rows.length === 0 ? (
          <p className="text-xs text-carbon-gray-50">Nothing in flight — no drafts are currently at CPSC.</p>
        ) : (
          <div className="overflow-x-auto">
            <CpscTable
              className="w-full text-xs"
              headClassName={CPSC_TH_CLASS}
              columns={[
                { key: 'report', label: 'Report No', className: 'py-1 pr-2 text-left' },
                { key: 'style', label: 'Style / Products', className: 'py-1 pr-2 text-left' },
                { key: 'type', label: 'Type', className: 'py-1 pr-2 text-left' },
                { key: 'status', label: 'Status', className: 'py-1 pr-2 text-left' },
                { key: 'errors', label: 'Errors', className: 'py-1 pr-2 text-left' },
              ]}
            >
              {rows.map((d) => (
                <tr key={d.id} className={CPSC_ROW_CLASS}>
                  <td className="py-1 pr-2 font-plex-mono font-medium text-carbon-ink">{s(d.reportNo) || s(d.draftId)}</td>
                  <td className="py-1 pr-2 text-carbon-gray-70">{s(d.style) || s(d.styleNo) || (Array.isArray(d.productList) ? d.productList.slice(0, 4).join(', ') + (d.productList.length > 4 ? '…' : '') : s(d.primaryProductId))}</td>
                  <td className="py-1 pr-2">{s(d.certificateType) || '—'}</td>
                  <td className="py-1 pr-2"><span className={tone(s(d.status))}>{(DRAFT_STATUS[s(d.status)] && DRAFT_STATUS[s(d.status)].label) || s(d.status)}</span></td>
                  <td className="py-1 pr-2 font-plex-mono text-carbon-gray-50">{(Array.isArray(d.submissionErrors) ? d.submissionErrors.length : 0) + (Array.isArray(d.certificationErrors) ? d.certificationErrors.length : 0) || '—'}</td>
                </tr>
              ))}
            </CpscTable>
          </div>
        )}
      </div>

      {/* The per-style checker tool (unchanged component — the same engine the dashboard uses) */}
      <CpscStatusChecker userProfile={userProfile} />
    </div>
  );
}
